import { useOrganization, useUser } from '@clerk/clerk-react'

import type {
  OrganizationInvitation,
  OrganizationMember,
  OrganizationRoleKey,
} from '@/components/org-settings/members/members.interface'

import type {
  ClerkInvitationsResource,
  ClerkMembershipsResource,
  ClerkOrganizationResource,
} from './members-clerk.types'
import { useInvitationOperations } from './use-invitation-operations.hook'
import { useLeaveOrganization } from './use-leave-organization.hook'
import { useMemberLimits } from './use-member-limits.hook'
import { useMemberOperations } from './use-member-operations.hook'
import { useMembersList } from './use-members-list.hook'
import { useMembersLoadingState } from './use-members-loading-state.hook'

const PAGE_SIZE = 50

export interface UseMembersSettingsReturn {
  memberList: OrganizationMember[]
  invitationList: OrganizationInvitation[]
  currentUserRole: OrganizationRoleKey | null
  currentUserId: string | null
  maxAllowedMembers: number | null
  currentMemberCount: number
  isAtMemberLimit: boolean
  canManageMembers: boolean
  isMembersLoading: boolean
  isInvitationsLoading: boolean
  isInviting: boolean
  handleInvite: (data: { email: string; memberRole: OrganizationRoleKey }) => Promise<void>
  handleRoleChange: (memberId: string, memberRole: OrganizationRoleKey) => Promise<void>
  handleRemoveMember: (memberId: string) => Promise<void>
  handleRevokeInvitation: (invitationId: string) => Promise<void>
  handleLeaveOrganization: () => Promise<void>
}

export const useMembersSettings = (): UseMembersSettingsReturn => {
  const { user } = useUser()
  const clerkOrg = useOrganization({
    memberships: { pageSize: PAGE_SIZE, keepPreviousData: true },
    invitations: { pageSize: PAGE_SIZE, keepPreviousData: true },
  })

  const currentUserId = user?.id ?? null
  const currentUserRole = (clerkOrg.membership?.role as OrganizationRoleKey) ?? null

  const typedMemberships = clerkOrg.memberships as ClerkMembershipsResource | null
  const typedInvitations = clerkOrg.invitations as ClerkInvitationsResource | null
  const typedOrganization = clerkOrg.organization as ClerkOrganizationResource | null

  const limits = useMemberLimits({
    maxAllowedMembers: clerkOrg.organization?.maxAllowedMemberships ?? null,
    currentMemberCount: clerkOrg.memberships?.data?.length ?? 0,
  })

  const lists = useMembersList({
    memberships: typedMemberships,
    invitations: typedInvitations,
    currentUserId,
  })
  const loading = useMembersLoadingState({
    memberships: clerkOrg.memberships,
    invitations: clerkOrg.invitations,
  })
  const memberOps = useMemberOperations({ memberships: typedMemberships, currentUserId })
  const invitationOps = useInvitationOperations({
    organization: typedOrganization,
    invitations: typedInvitations,
    isAtMemberLimit: limits.isAtMemberLimit,
  })
  const leaveOps = useLeaveOrganization({ memberships: typedMemberships, currentUserId })

  return {
    ...lists,
    ...limits,
    ...loading,
    currentUserRole,
    currentUserId,
    canManageMembers: currentUserRole === 'org:admin',
    isInviting: invitationOps.isInviting,
    handleInvite: invitationOps.handleInvite,
    handleRoleChange: memberOps.handleRoleChange,
    handleRemoveMember: memberOps.handleRemoveMember,
    handleRevokeInvitation: invitationOps.handleRevokeInvitation,
    handleLeaveOrganization: leaveOps.handleLeaveOrganization,
  }
}
